import { Component, Input, DoCheck } from '@angular/core';
import { ProjectsService } from 'src/app/api/projects.service';
import * as dayjs from 'dayjs';

@Component({
  selector: 'app-client-schedule-preview',
  templateUrl: './client-schedule-preview.component.html',
  styleUrls: ['./client-schedule-preview.component.scss'],
})
export class ClientSchedulePreviewComponent implements DoCheck {

  private _project: any = null;
  @Input() set project(value: any) {
    this._project = value;
    this.AutoSelectIfSingle();
  }
  get project() { return this._project; }
  @Input() small_mode: boolean = false;

  selectedPreviewClient: any = null;
  selectedPreviewIsCompany: boolean = false;
  clientSchedulePreview: any[] = [];

  private _lastRulesSnapshot: string = '';

  constructor(private projectsService: ProjectsService) {}

  ngDoCheck() {
    const snapshot = JSON.stringify(this._project?.repeat_rules);
    if (snapshot !== this._lastRulesSnapshot) {
      this._lastRulesSnapshot = snapshot;
      this.Refresh();
    }
  }

  AutoSelectIfSingle() {
    const clients = this._project?.assigned_clients || [];
    const companies = this._project?.assigned_companies || [];
    if (this.selectedPreviewClient) return;
    if (clients.length > 0) this.SelectPreviewClient(clients[0], false);
    else if (companies.length > 0) this.SelectPreviewClient(companies[0], true);
  }

  Refresh() {
    if (this.selectedPreviewClient) {
      this.clientSchedulePreview = this.ComputeClientSchedulePreview(this.selectedPreviewClient);
    }
  }

  SelectPreviewClient(entity: any, is_company: boolean = false) {
    this.selectedPreviewClient = entity;
    this.selectedPreviewIsCompany = is_company;
    this.clientSchedulePreview = this.ComputeClientSchedulePreview(entity);
  }

  ComputeClientSchedulePreview(entity: any): any[] {
    if (!this.project?.repeat) return [];

    const rules = JSON.parse(JSON.stringify(this.project.repeat_rules));
    const originalRepeatStart = rules.repeat_start_date;

    if (rules.relative_date_type === 'year_end_date' && entity.year_end_date) {
      const yed = dayjs(entity.year_end_date);
      const yedMonth = yed.month();
      const yedDay = yed.date();
      const thisYearYed = dayjs().year(dayjs().year()).month(yedMonth).date(yedDay);

      if (rules.frequency === 'monthly' || rules.frequency === 'quarterly' || rules.frequency === 'semi-annually') {
        if (thisYearYed.isAfter(dayjs(rules.repeat_start_date))) {
          const monthsDiff = thisYearYed.diff(dayjs(rules.repeat_start_date), 'month');
          const intervalsBack = Math.ceil(monthsDiff / rules.interval);
          rules.repeat_start_date = thisYearYed.subtract(intervalsBack * rules.interval, 'month').format('YYYY-MM-DD');
        } else {
          rules.repeat_start_date = thisYearYed.format('YYYY-MM-DD');
        }
        rules.monthly_type = 'day';
        rules.monthly_day = yedDay;
      } else if (rules.frequency === 'yearly') {
        if (thisYearYed.isAfter(dayjs(rules.repeat_start_date))) {
          const interval = Math.max(1, rules.interval || 1);
          const yearsDiff = thisYearYed.diff(dayjs(rules.repeat_start_date), 'year');
          const intervalsBack = Math.ceil(yearsDiff / interval);
          rules.repeat_start_date = thisYearYed.subtract(intervalsBack * interval, 'year').format('YYYY-MM-DD');
        } else {
          rules.repeat_start_date = thisYearYed.format('YYYY-MM-DD');
        }
        rules.yearly_month = yedMonth + 1;
        rules.yearly_day = yedDay;
        rules.yearly_use_ordinal = false;
      }

      const startDj = dayjs(originalRepeatStart);
      let rsd = dayjs(rules.repeat_start_date);
      if (rsd.isBefore(startDj)) {
        const unit = rules.frequency === 'yearly' ? 'year' : 'month';
        const diff = startDj.diff(rsd, unit);
        const steps = Math.ceil(diff / rules.interval);
        rsd = rsd.add(steps * rules.interval, unit);
        if (rsd.isBefore(startDj)) rsd = rsd.add(rules.interval, unit);
        rules.repeat_start_date = rsd.format('YYYY-MM-DD');
      }
    }

    try {
      const windowStart = dayjs(originalRepeatStart).toISOString();
      const windowEnd = dayjs().add(5, 'year').toISOString();
      const occurrences = this.projectsService.buildOccurrences(rules, rules.repeat_start_date, windowStart, windowEnd);
      const limited = occurrences.slice(0, 12);

      const isMod = (before: boolean) => before ? -1 : 1;

      return limited.map(occ => ({
        occurrence_date: occ.format('YYYY-MM-DD'),
        internal_start: this.AddOffset(occ.add(rules.internal_start_offset * isMod(rules.internal_start_date_before), rules.internal_start_offset_unit), rules.internal_start_offset_type),
        internal_end: this.AddOffset(occ.add(rules.internal_end_offset * isMod(rules.internal_end_date_before), rules.internal_end_offset_unit), rules.internal_end_offset_type),
        external_start: this.AddOffset(occ.add(rules.external_start_offset * isMod(rules.external_start_date_before), rules.external_start_offset_unit), rules.external_start_offset_type),
        external_end: this.AddOffset(occ.add(rules.external_end_offset * isMod(rules.external_end_date_before), rules.external_end_offset_unit), rules.external_end_offset_type),
        period_start: this.AddOffset(occ.add(rules.period_start_offset * isMod(rules.period_start_date_before), rules.period_start_offset_unit), rules.period_start_offset_type),
        period_end: this.AddOffset(occ.add(rules.period_end_offset * isMod(rules.period_end_date_before), rules.period_end_offset_unit), rules.period_end_offset_type),
      }));
    } catch (e) {
      console.error('Error computing client schedule preview:', e);
      return [];
    }
  }

  AddOffset(date, offsetType) {
    if (offsetType === 'start_of_month') return date.startOf('month').format('YYYY-MM-DD');
    if (offsetType === 'end_of_month') return date.endOf('month').format('YYYY-MM-DD');
    return date.format('YYYY-MM-DD');
  }
}
